import {
  type CatalogItemDto,
  datePl,
  MANUAL_DOC_KINDS,
  type MeDto,
  packToStock,
  type ResolvedCodeDto,
  STOCK_DOC_KINDS,
  type StockDocKind,
  type StockDocumentDto,
  stockDocumentInputSchema,
  type StockDocumentListDto,
  todayInWarsaw,
} from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Button, Card, Drawer, Group, Menu, Modal, NumberInput, Pagination, Select, SimpleGrid, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconArrowBackUp, IconFileImport, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorText } from '../../api';
import { can } from '../../auth';
import { ask } from '../../confirm';
import { StockDocKindBadge, StockDocStatusBadge } from '../../components/Badges';
import { Money, MoneyInput } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { useWarehouses } from '../../data';
import { formatDateTime, qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { CaseSelect, ItemSelect, useRecipients, useStockItems, WarehouseSelect, whLabel } from './common';

const PAGE = 50;

/** Dokumenty magazynowe (docs/14 §2.4): PZ, WZ, WE, ZW, MM, BO, KOR, INW — lista, zapis, zatwierdzenie, odwrócenie. */
export function DocumentsPage({ me }: { me: MeDto }) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 300);
  const [kind, setKind] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<{ kind: StockDocKind; doc: StockDocumentDto | null } | null>(null);
  const whs = useWarehouses();
  const openId = params.get('dokument');
  const qs = new URLSearchParams({ limit: String(PAGE), offset: String((page - 1) * PAGE) });
  if (debounced.trim()) qs.set('q', debounced.trim());
  if (kind) qs.set('kind', kind);
  if (status) qs.set('status', status);
  if (warehouseId) qs.set('warehouseId', warehouseId);
  const list = useQuery({ queryKey: ['stock-documents', qs.toString()], queryFn: () => api<StockDocumentListDto>(`/stock-documents?${qs}`) });
  const canMove = can(me, 'magazyn.ruchy');
  const canFix = can(me, 'magazyn.korekty');
  const kinds = MANUAL_DOC_KINDS.filter((k) => (k === 'KOR' ? canFix : canMove));

  // ?nowy=PZ — skrót z innych ekranów
  useEffect(() => {
    const n = params.get('nowy') as StockDocKind | null;
    if (n && kinds.includes(n)) {
      setEditing({ kind: n, doc: null });
      params.delete('nowy');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (id: string | null) => {
    if (id) params.set('dokument', id);
    else params.delete('dokument');
    setParams(params, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Dokumenty magazynowe"
        description="Przyjęcia, wydania, zwroty, przesunięcia i korekty. Zatwierdzony dokument zmienia stan i jest niezmienny — pomyłkę poprawia się dokumentem odwracającym."
        actions={
          kinds.length > 0 && (
            <Menu position="bottom-end">
              <Menu.Target>
                <Button leftSection={<IconPlus size={16} />}>Nowy dokument</Button>
              </Menu.Target>
              <Menu.Dropdown>
                {kinds.map((k) => (
                  <Menu.Item key={k} leftSection={<StockDocKindBadge kind={k} size="xs" />} onClick={() => setEditing({ kind: k, doc: null })}>
                    {STOCK_DOC_KINDS[k].label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )
        }
      />
      <Group mb="md" wrap="wrap">
        <TextInput placeholder="Szukaj: numer, dostawca, produkt…" leftSection={<IconSearch size={16} />} value={q} onChange={(e) => (setQ(e.currentTarget.value), setPage(1))} w={{ base: '100%', sm: 280 }} />
        <Select
          placeholder="Rodzaj"
          clearable
          data={Object.entries(STOCK_DOC_KINDS).map(([value, d]) => ({ value, label: `${value} — ${d.label}` }))}
          value={kind}
          onChange={(v) => (setKind(v), setPage(1))}
          w={{ base: '100%', sm: 240 }}
        />
        <Select
          placeholder="Status"
          clearable
          data={[
            { value: 'roboczy', label: 'Robocze' },
            { value: 'zatwierdzony', label: 'Zatwierdzone' },
          ]}
          value={status}
          onChange={(v) => (setStatus(v), setPage(1))}
          w={{ base: '100%', sm: 160 }}
        />
        {(whs.data?.length ?? 0) > 1 && (
          <Select placeholder="Wszystkie magazyny" clearable data={(whs.data ?? []).map((w) => ({ value: w.id, label: whLabel(w) }))} value={warehouseId} onChange={(v) => (setWarehouseId(v), setPage(1))} w={{ base: '100%', sm: 260 }} />
        )}
      </Group>
      <Card padding={0}>
        <QueryState isLoading={list.isLoading} error={list.error}>
          {list.data?.rows.length === 0 ? (
            <EmptyState title="Brak dokumentów" hint={canMove ? 'Zacznij od stanu początkowego (BO) albo przyjęcia dostawy (PZ).' : undefined} />
          ) : (
            <Table.ScrollContainer minWidth={820}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Dokument</Table.Th>
                    <Table.Th>Data</Table.Th>
                    <Table.Th>Magazyn</Table.Th>
                    <Table.Th>Dla / od</Table.Th>
                    <Table.Th ta="right">Pozycji</Table.Th>
                    <Table.Th ta="right">Wartość</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {list.data?.rows.map((d) => (
                    <Table.Tr key={d.id} className="row-link" onClick={() => open(d.id)}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <StockDocKindBadge kind={d.kind} />
                          <Text size="sm" fw={600}>
                            {d.number ?? 'roboczy'}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>{datePl(d.docDate)}</Table.Td>
                      <Table.Td>
                        {d.companyCode} · {d.warehouseCode}
                        {d.targetWarehouseCode ? ` → ${d.targetWarehouseCode}` : ''}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{d.caseNumber ?? d.teamName ?? (d.counterparty || '—')}</Text>
                      </Table.Td>
                      <Table.Td ta="right">{d.lineCount}</Table.Td>
                      <Table.Td ta="right">
                        <Money gr={d.valueGr} />
                      </Table.Td>
                      <Table.Td>
                        <StockDocStatusBadge status={d.status} reversed={!!d.reversedBy} />
                        {d.reversalOf && (
                          <Text size="xs" c="dimmed">
                            odwrócenie
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          {(list.data?.total ?? 0) > PAGE && (
            <Group justify="center" p="sm">
              <Pagination total={Math.ceil(list.data!.total / PAGE)} value={page} onChange={setPage} size="sm" />
            </Group>
          )}
        </QueryState>
      </Card>
      <DocumentDrawer id={openId} onClose={() => open(null)} onEdit={(doc) => setEditing({ kind: doc.kind, doc })} me={me} />
      {editing && (
        <DocumentEditor
          kind={editing.kind}
          doc={editing.doc}
          me={me}
          onClose={() => setEditing(null)}
          onSaved={(d) => {
            setEditing(null);
            open(d.id);
          }}
        />
      )}
    </>
  );
}

type Line = { key: string; itemId: string | null; packId: string | null; packQty: number | null; quantity: number | null; unitPriceGr: number | null; note: string };
let seq = 0;
const newLine = (): Line => ({ key: `l${++seq}`, itemId: null, packId: null, packQty: null, quantity: null, unitPriceGr: null, note: '' });
const newKey = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k${Date.now()}${Math.random()}`);

/** Formularz dokumentu (nowy lub roboczy). „Zatwierdź” zmienia stan; „Zapisz roboczy” — nie. */
function DocumentEditor({ kind, doc, me, onClose, onSaved }: { kind: StockDocKind; doc: StockDocumentDto | null; me: MeDto; onClose: () => void; onSaved: (d: StockDocumentDto) => void }) {
  const def = STOCK_DOC_KINDS[kind];
  const whs = useWarehouses();
  const items = useStockItems();
  const recipients = useRecipients();
  const seeValue = can(me, 'ceny.zakup');
  const defaultWh = whs.data?.find((w) => w.active && w.isDefault)?.id ?? whs.data?.find((w) => w.active)?.id ?? null;
  const [warehouseId, setWarehouseId] = useState<string | null>(doc?.warehouseId ?? defaultWh);
  const [targetWarehouseId, setTarget] = useState<string | null>(doc?.targetWarehouseId ?? null);
  const [docDate, setDocDate] = useState(doc?.docDate ?? todayInWarsaw());
  const [counterparty, setCounterparty] = useState(doc?.counterparty ?? '');
  const [externalNumber, setExternalNumber] = useState(doc?.externalNumber ?? '');
  const [currency, setCurrency] = useState(doc?.currency ?? 'PLN');
  const [exchangeRate, setExchangeRate] = useState<number>(doc?.exchangeRate ?? 1);
  const [caseId, setCaseId] = useState<string | null>(doc?.caseId ?? null);
  const [caseLabel, setCaseLabel] = useState<string | null>(doc?.caseNumber ?? null);
  const [teamId, setTeamId] = useState<string | null>(doc?.teamId ?? null);
  const [recipientUserId, setRecipient] = useState<string | null>(doc?.recipientUserId ?? null);
  const [sourceDocumentId, setSource] = useState<string | null>(doc?.sourceDocumentId ?? null);
  const [reason, setReason] = useState(doc?.reason ?? '');
  const [note, setNote] = useState(doc?.note ?? '');
  const [lines, setLines] = useState<Line[]>(
    doc ? doc.lines.map((l) => ({ key: l.id, itemId: l.itemId, packId: l.packId, packQty: l.packQty, quantity: l.quantity, unitPriceGr: l.unitPriceGr, note: l.note })) : [newLine()],
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const requestKey = useMemo(newKey, []);
  const qc = useQueryClient();
  useEffect(() => {
    if (!warehouseId && defaultWh) setWarehouseId(defaultWh);
  }, [defaultWh, warehouseId]);
  const wh = whs.data?.find((w) => w.id === warehouseId);
  const sources = useQuery({
    queryKey: ['stock-documents', 'issues', wh?.companyId],
    queryFn: async () => {
      const [a, b] = await Promise.all(['WZ', 'WE'].map((k) => api<StockDocumentListDto>(`/stock-documents?kind=${k}&status=zatwierdzony&limit=100&companyId=${wh!.companyId}`)));
      return [...a!.rows, ...b!.rows].filter((d) => !d.reversalOf && !d.reversedBy);
    },
    enabled: kind === 'ZW' && !!wh,
  });
  const itemById = (id: string | null) => items.data?.find((i) => i.id === id);
  const setLine = (key: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const team = recipients.data?.find((t) => t.id === teamId);
  const priced = def.needsPrice || kind === 'KOR';

  const save = useMutation({
    mutationFn: (approve: boolean) => {
      const body = {
        kind,
        warehouseId,
        targetWarehouseId: kind === 'MM' ? targetWarehouseId : null,
        docDate,
        reason,
        note,
        counterparty,
        externalNumber,
        currency,
        exchangeRate,
        caseId: kind === 'WZ' ? caseId : null,
        teamId: ['WZ', 'WE', 'ZW'].includes(kind) ? teamId : null,
        recipientUserId: ['WZ', 'WE'].includes(kind) ? recipientUserId : null,
        sourceDocumentId: kind === 'ZW' ? sourceDocumentId : null,
        lines: lines
          .filter((l) => l.itemId)
          .map((l) => {
            const it = itemById(l.itemId);
            const pack = it?.packs.find((p) => p.id === l.packId);
            return {
              itemId: l.itemId,
              packId: pack ? pack.id : null,
              packQty: pack ? l.packQty : null,
              quantity: pack && l.packQty ? packToStock(l.packQty, pack.factor) : (l.quantity ?? 0),
              unitPriceGr: priced ? l.unitPriceGr : null,
              note: l.note,
            };
          }),
        approve,
        requestKey: doc ? null : `${requestKey}-${approve ? 'z' : 'r'}`,
      };
      const r = stockDocumentInputSchema.safeParse(body);
      if (!r.success) throw new Error([...new Set(r.error.issues.map((i) => i.message))].join('\n'));
      return doc ? api<StockDocumentDto>(`/stock-documents/${doc.id}`, { method: 'PUT', body: r.data }) : api<StockDocumentDto>('/stock-documents', { method: 'POST', body: r.data });
    },
    onSuccess: (d, approve) => {
      notifyOk(approve ? `Zatwierdzono ${d.number}` : 'Zapisano dokument roboczy');
      void qc.invalidateQueries({ queryKey: ['stock-documents'] });
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['stock-item'] });
      void qc.invalidateQueries({ queryKey: ['catalog'] });
      onSaved(d);
    },
    onError: (e) => setErrors([errorText(e)]),
  });

  return (
    <Modal opened onClose={onClose} size="90rem" title={`${doc ? 'Edycja dokumentu' : 'Nowy dokument'}: ${def.label} (${kind})`}>
      <Stack>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <WarehouseSelect label={kind === 'MM' ? 'Z magazynu' : 'Magazyn'} value={warehouseId} onChange={setWarehouseId} />
          {kind === 'MM' && <WarehouseSelect label="Do magazynu (ta sama firma)" value={targetWarehouseId} onChange={setTarget} companyId={wh?.companyId} exclude={warehouseId} />}
          <TextInput label="Data dokumentu" type="date" max={todayInWarsaw()} value={docDate} onChange={(e) => setDocDate(e.currentTarget.value)} />
          {(kind === 'PZ' || kind === 'BO') && <TextInput label={kind === 'PZ' ? 'Dostawca' : 'Źródło (opis)'} value={counterparty} onChange={(e) => setCounterparty(e.currentTarget.value)} />}
          {kind === 'PZ' && <TextInput label="Nr dokumentu dostawy" value={externalNumber} onChange={(e) => setExternalNumber(e.currentTarget.value)} />}
          {kind === 'PZ' && seeValue && (
            <Group grow>
              <Select label="Waluta" data={['PLN', 'EUR', 'USD', 'CZK']} value={currency} onChange={(v) => v && setCurrency(v)} allowDeselect={false} />
              {currency !== 'PLN' && <NumberInput label="Kurs" decimalScale={4} decimalSeparator="," min={0.0001} value={exchangeRate} onChange={(v) => setExchangeRate(Number(v) || 1)} />}
            </Group>
          )}
          {kind === 'WZ' && <CaseSelect value={caseId} label={caseLabel} onChange={(id, l) => (setCaseId(id), setCaseLabel(l))} />}
          {['WZ', 'WE', 'ZW'].includes(kind) && (
            <Select label="Ekipa" clearable data={(recipients.data ?? []).map((t) => ({ value: t.id, label: t.name }))} value={teamId} onChange={(v) => (setTeamId(v), setRecipient(null))} />
          )}
          {['WZ', 'WE'].includes(kind) && (
            <Select
              label="Odbierający"
              clearable
              searchable
              data={(team ? team.members : (recipients.data ?? []).flatMap((t) => t.members)).filter((m, i, a) => a.findIndex((x) => x.id === m.id) === i).map((m) => ({ value: m.id, label: m.name }))}
              value={recipientUserId}
              onChange={setRecipient}
            />
          )}
          {kind === 'ZW' && (
            <Select
              label="Zwrot do wydania"
              clearable
              searchable
              placeholder="opcjonalnie — wtedy wycena ceną wydania"
              data={(sources.data ?? []).map((d) => ({ value: d.id, label: `${d.number} — ${d.caseNumber ?? d.teamName ?? ''} (${datePl(d.docDate)})` }))}
              value={sourceDocumentId}
              onChange={setSource}
            />
          )}
        </SimpleGrid>
        {(def.needsReason || kind === 'KOR') && (
          <Textarea label="Przyczyna korekty" required autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} description="Ilość dodatnia — nadwyżka, ujemna — niedobór. Przyczyna trafia do historii." />
        )}
        <SectionCard
          title="Pozycje"
          padding="sm"
          actions={
            kind === 'BO' && (
              <Button size="xs" variant="light" leftSection={<IconFileImport size={14} />} onClick={() => setImportOpen(true)}>
                Wczytaj z CSV
              </Button>
            )
          }
        >
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing={4}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Produkt</Table.Th>
                  <Table.Th w={260}>Ilość</Table.Th>
                  {priced && seeValue && <Table.Th w={150}>Cena netto / jedn.</Table.Th>}
                  <Table.Th w={36} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((l) => {
                  const it = itemById(l.itemId);
                  const packs = (it?.packs ?? []).filter((p) => p.active || p.id === l.packId);
                  const pack = packs.find((p) => p.id === l.packId);
                  return (
                    <Table.Tr key={l.key}>
                      <Table.Td>
                        <ItemSelect items={items.data ?? []} value={l.itemId} onChange={(v) => setLine(l.key, { itemId: v, packId: null, packQty: null })} size="xs" placeholder="Wybierz produkt" />
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          {pack ? (
                            <NumberInput size="xs" w={80} min={0.001} decimalScale={3} decimalSeparator="," hideControls value={l.packQty ?? ''} onChange={(v) => setLine(l.key, { packQty: v === '' ? null : Number(v) })} />
                          ) : (
                            <NumberInput
                              size="xs"
                              w={100}
                              decimalScale={3}
                              decimalSeparator=","
                              hideControls
                              allowNegative={kind === 'KOR'}
                              value={l.quantity ?? ''}
                              onChange={(v) => setLine(l.key, { quantity: v === '' ? null : Number(v) })}
                            />
                          )}
                          {packs.length > 0 && (def.needsPrice || kind === 'PZ') ? (
                            <Select
                              size="xs"
                              w={150}
                              data={[{ value: '', label: it?.unit ?? 'jedn.' }, ...packs.map((p) => ({ value: p.id, label: `${p.name} (${qtyPl(p.factor)} ${it?.unit})` }))]}
                              value={l.packId ?? ''}
                              onChange={(v) => setLine(l.key, { packId: v || null })}
                              allowDeselect={false}
                            />
                          ) : (
                            <Text size="xs" c="dimmed">
                              {it?.unit}
                            </Text>
                          )}
                        </Group>
                        {pack && l.packQty ? (
                          <Text size="xs" c="dimmed">
                            = {qtyPl(packToStock(l.packQty, pack.factor))} {it?.unit}
                          </Text>
                        ) : null}
                      </Table.Td>
                      {priced && seeValue && (
                        <Table.Td>
                          <MoneyInput size="xs" valueGr={l.unitPriceGr} onChangeGr={(v) => setLine(l.key, { unitPriceGr: v })} placeholder={kind === 'KOR' ? 'średnia' : undefined} />
                          {pack && l.unitPriceGr !== null && (
                            <Text size="xs" c="dimmed">
                              opakowanie: <Money gr={Math.round(l.unitPriceGr * pack.factor)} />
                            </Text>
                          )}
                        </Table.Td>
                      )}
                      <Table.Td>
                        <ActionIcon variant="subtle" color="red" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [newLine()]))} aria-label="Usuń pozycję">
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Button size="xs" variant="light" mt="xs" leftSection={<IconPlus size={14} />} onClick={() => setLines((ls) => [...ls, newLine()])}>
            Dodaj pozycję
          </Button>
          {priced && seeValue && currency !== 'PLN' && (
            <Text size="xs" c="dimmed" mt={4}>
              Ceny w {currency}; wartość magazynowa w PLN po kursie {exchangeRate}.
            </Text>
          )}
        </SectionCard>
        <Textarea label="Uwagi" autosize minRows={1} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        {errors.length > 0 && (
          <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
            {errors.join('\n')}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button variant="light" onClick={() => save.mutate(false)} loading={save.isPending && save.variables === false}>
            Zapisz roboczy
          </Button>
          <Button onClick={() => save.mutate(true)} loading={save.isPending && save.variables === true}>
            Zatwierdź
          </Button>
        </Group>
      </Stack>
      <CsvImport
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        items={items.data ?? []}
        onLines={(ls) => {
          setLines((cur) => [...cur.filter((l) => l.itemId), ...ls]);
          setImportOpen(false);
        }}
      />
    </Modal>
  );
}

/** Import stanu początkowego: wiersze „kod;ilość;cena” (kod produktu albo EAN; cena netto za jednostkę). */
function CsvImport({ opened, onClose, items, onLines }: { opened: boolean; onClose: () => void; items: CatalogItemDto[]; onLines: (l: Line[]) => void }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ ok: Line[]; bad: string[] } | null>(null);
  const resolve = useMutation({
    mutationFn: async () => {
      const rows = text
        .split(/\r?\n/)
        .map((r) => r.trim())
        .filter(Boolean)
        .map((r) => r.split(/[;\t]/).map((c) => c.trim()));
      const data = rows.filter((r) => r.length >= 2 && /\d/.test(r[1] ?? ''));
      const codes = data.map((r) => r[0]!);
      const found = codes.length ? await api<ResolvedCodeDto[]>('/stock-documents/resolve-codes', { method: 'POST', body: { codes } }) : [];
      const num = (s: string | undefined) => (s ? Number(s.replace(/\s/g, '').replace(',', '.')) : NaN);
      const ok: Line[] = [];
      const bad: string[] = [];
      for (const r of data) {
        const f = found.find((x) => x.code === r[0]);
        const q = num(r[1]);
        const price = num(r[2]);
        if (!f?.itemId) bad.push(`${r[0]}: nie znaleziono produktu (kod lub EAN)`);
        else if (!f.stocked) bad.push(`${r[0]}: produkt bez stanu magazynowego`);
        else if (!(q > 0)) bad.push(`${r[0]}: nieprawidłowa ilość`);
        else ok.push({ ...newLine(), itemId: f.itemId, quantity: q, unitPriceGr: Number.isFinite(price) ? Math.round(price * 10000) / 100 : null });
      }
      return { ok, bad };
    },
    onSuccess: setResult,
    onError: notifyError,
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Import stanu początkowego z CSV" size="lg">
      <Stack>
        <Text size="sm">
          Wklej wiersze z arkusza: <b>kod;ilość;cena</b> (kod produktu lub EAN, ilość w jednostce magazynowej, cena netto za jednostkę w zł). Separator: średnik lub tabulator.
        </Text>
        <Textarea autosize minRows={6} maxRows={14} placeholder={'PC-HX8;2;19800\nRURA-PEX16;250;2,35'} value={text} onChange={(e) => (setText(e.currentTarget.value), setResult(null))} />
        {result && (
          <Alert color={result.bad.length ? 'orange' : 'green'} style={{ whiteSpace: 'pre-line' }}>
            Rozpoznano pozycji: {result.ok.length}
            {result.bad.length ? `\nPominięto:\n${result.bad.join('\n')}` : ''}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          {result ? (
            <Button onClick={() => onLines(result.ok)} disabled={!result.ok.length}>
              Dodaj {result.ok.length} pozycji
            </Button>
          ) : (
            <Button onClick={() => resolve.mutate()} loading={resolve.isPending} disabled={!text.trim() || !items}>
              Sprawdź
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

/** Szczegóły dokumentu: pozycje, powiązania, działania (zatwierdź, usuń roboczy, odwróć zatwierdzony). */
function DocumentDrawer({ id, onClose, onEdit, me }: { id: string | null; onClose: () => void; onEdit: (d: StockDocumentDto) => void; me: MeDto }) {
  const q = useQuery({ queryKey: ['stock-document', id], queryFn: () => api<StockDocumentDto>(`/stock-documents/${id}`), enabled: !!id });
  const qc = useQueryClient();
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reason, setReason] = useState('');
  const d = q.data;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['stock-documents'] });
    void qc.invalidateQueries({ queryKey: ['stock-document'] });
    void qc.invalidateQueries({ queryKey: ['stock'] });
    void qc.invalidateQueries({ queryKey: ['stock-item'] });
  };
  const approve = useMutation({
    mutationFn: () => api<StockDocumentDto>(`/stock-documents/${id}/approve`, { method: 'POST' }),
    onSuccess: (r) => (notifyOk(`Zatwierdzono ${r.number}`), refresh()),
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: () => api(`/stock-documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => (notifyOk('Usunięto dokument roboczy'), refresh(), onClose()),
    onError: notifyError,
  });
  const reverse = useMutation({
    mutationFn: () => api<StockDocumentDto>(`/stock-documents/${id}/reverse`, { method: 'POST', body: { reason } }),
    onSuccess: (r) => {
      notifyOk(`Utworzono dokument odwracający ${r.number}`);
      setReverseOpen(false);
      setReason('');
      refresh();
    },
    onError: notifyError,
  });
  const seeValue = can(me, 'ceny.zakup');
  return (
    <Drawer opened={!!id} onClose={onClose} position="right" size="xl" title={d ? `${STOCK_DOC_KINDS[d.kind].label} ${d.number ?? '(roboczy)'}` : 'Dokument'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <Stack>
            <Group gap="xs">
              <StockDocKindBadge kind={d.kind} />
              <StockDocStatusBadge status={d.status} reversed={!!d.reversedBy} />
              <Text size="sm" c="dimmed">
                {datePl(d.docDate)} · {d.companyCode} · {d.warehouseCode}
                {d.targetWarehouseCode ? ` → ${d.targetWarehouseCode}` : ''}
              </Text>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              {d.counterparty && <Info label="Dostawca / źródło" value={d.counterparty} />}
              {d.externalNumber && <Info label="Dokument dostawy" value={d.externalNumber} />}
              {d.caseNumber && <Info label="Sprawa" value={<Anchor component={Link} to={`/sprawy/${d.caseId}`}>{d.caseNumber}</Anchor>} />}
              {d.teamName && <Info label="Ekipa" value={d.teamName} />}
              {d.recipientName && <Info label="Odbierający" value={d.recipientName} />}
              {d.sourceDocumentNumber && <Info label="Zwrot do" value={<Anchor component={Link} to={`/magazyn/dokumenty?dokument=${d.sourceDocumentId}`}>{d.sourceDocumentNumber}</Anchor>} />}
              {d.reversalOfNumber && <Info label="Odwraca dokument" value={<Anchor component={Link} to={`/magazyn/dokumenty?dokument=${d.reversalOf}`}>{d.reversalOfNumber}</Anchor>} />}
              {d.reversedBy && <Info label="Odwrócony dokumentem" value={<Anchor component={Link} to={`/magazyn/dokumenty?dokument=${d.reversedBy.id}`}>{d.reversedBy.number}</Anchor>} />}
              {d.currency !== 'PLN' && <Info label="Waluta" value={`${d.currency}, kurs ${d.exchangeRate}`} />}
              {d.reason && <Info label="Przyczyna" value={d.reason} />}
              {d.note && <Info label="Uwagi" value={d.note} />}
              <Info label="Utworzył" value={`${d.createdByName ?? '—'}, ${formatDateTime(d.createdAt)}`} />
              {d.approvedAt && <Info label="Zatwierdził" value={`${d.approvedByName ?? '—'}, ${formatDateTime(d.approvedAt)}`} />}
            </SimpleGrid>
            <Table.ScrollContainer minWidth={520}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Produkt</Table.Th>
                    <Table.Th ta="right">Ilość</Table.Th>
                    {seeValue && <Table.Th ta="right">Cena</Table.Th>}
                    {seeValue && <Table.Th ta="right">Wartość</Table.Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {d.lines.map((l) => (
                    <Table.Tr key={l.id}>
                      <Table.Td>
                        <Text size="sm">{l.name}</Text>
                        <Text size="xs" c="dimmed">
                          {l.code}
                          {l.packName ? ` · ${qtyPl(l.packQty ?? 0)} × ${l.packName}` : ''}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {qtyPl(l.quantity)} {l.unit}
                      </Table.Td>
                      {seeValue && (
                        <Table.Td ta="right">
                          <Money gr={l.unitPriceGr === null ? null : Math.round(l.unitPriceGr)} />
                        </Table.Td>
                      )}
                      {seeValue && (
                        <Table.Td ta="right">
                          <Money gr={l.valueGr} />
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="flex-end">
              {d.canEdit && (
                <Button
                  variant="subtle"
                  color="red"
                  onClick={async () => {
                    if (await ask('Dokument roboczy nie zmienił stanu — zostanie usunięty.', { title: 'Usunąć dokument roboczy?', confirm: 'Usuń', danger: true })) remove.mutate();
                  }}
                >
                  Usuń roboczy
                </Button>
              )}
              {d.canEdit && (
                <Button variant="light" onClick={() => onEdit(d)}>
                  Edytuj
                </Button>
              )}
              {d.canApprove && (
                <Button onClick={() => approve.mutate()} loading={approve.isPending}>
                  Zatwierdź
                </Button>
              )}
              {d.canReverse && (
                <Button variant="light" color="red" leftSection={<IconArrowBackUp size={16} />} onClick={() => setReverseOpen(true)}>
                  Odwróć dokument
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </QueryState>
      <Modal opened={reverseOpen} onClose={() => setReverseOpen(false)} title="Odwrócenie dokumentu">
        <Stack>
          <Text size="sm">Powstanie nowy dokument z ruchami przeciwnymi po tych samych cenach. Oryginał zostaje w historii bez zmian.</Text>
          <Textarea label="Przyczyna" required autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setReverseOpen(false)}>
              Anuluj
            </Button>
            <Button color="red" onClick={() => reverse.mutate()} loading={reverse.isPending} disabled={reason.trim().length < 5}>
              Odwróć
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" component="div">
        {value}
      </Text>
    </div>
  );
}
